import React from 'react'

import { Box, Button, CardHeader } from '@mui/material'

import { isDomain } from '@albacross/frontend-utils'

import { Flag } from 'components/_base'
import { DescriptionListTable } from 'components/table'
import { withForm } from 'decorators/withForm'
import { useUpdateCompany } from 'hooks/useCompany'
import { findLinkedInIndustryByCode } from 'utils/industries'
import { findNaceCode } from 'utils/nace'

import FormTextField from '../input/FormTextField'

type CompanyDetailsProps = {
  data: any
  handleSubmit?: (...args: any[]) => any
}

const getCompanyWebsiteField = website =>
  website ? (
    <a href={`//${website}`} rel="noopener noreferrer" target="_blank">
      {website}
    </a>
  ) : (
    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <Box sx={{ flex: 1, mr: '2rem' }}>
        <FormTextField name="website" />
      </Box>
      <Button size="small" type="submit" variant="outlined">
        Save
      </Button>
    </Box>
  )

const formatLink = url =>
  url && (
    <a href={url} rel="noopener noreferrer" target="_blank">
      {url}
    </a>
  )

const config = [
  {
    title: 'Name',
    field: 'name',
  },
  {
    title: 'Alias',
    field: 'alias',
  },
  {
    title: 'Number',
    field: 'number',
  },
  {
    title: 'UUID',
    field: 'uuid',
  },
  {
    title: 'Website',
    render: ({ website }) => getCompanyWebsiteField(website),
  },
  {
    title: 'Country',
    render: ({ country }) => <Flag country={country} />,
  },
  {
    title: 'City',
    field: 'address.city',
  },
  {
    title: 'Address',
    field: 'address.address',
  },
  {
    title: 'State',
    field: 'address.state',
  },
  {
    title: 'Zip Code',
    field: 'address.zipCode',
  },
  {
    title: 'Type',
    field: 'address.type',
  },
  {
    title: 'Phone country',
    render: ({ address: { phoneNumber } }) => <Flag country={phoneNumber?.country} />,
  },
  {
    title: 'Phone country code',
    field: 'address.phoneNumber.countryCode',
  },
  {
    title: 'Phone number',
    field: 'address.phoneNumber.number',
  },
  {
    title: 'Nace code (Industry)',
    render: ({ naceCode }) => findNaceCode(naceCode)?.name || '',
  },
  {
    title: 'Linkedin Industry',
    render: ({ linkedInIndustryCode }) => findLinkedInIndustryByCode(linkedInIndustryCode)?.category || '',
  },
  {
    title: 'Year founded',
    field: 'foundedAt',
  },
  {
    title: 'Description',
    field: 'description',
  },
  {
    title: 'Financial Report year',
    field: 'financialReport.year',
  },
  {
    title: 'Financial Report currency',
    field: 'financialReport.currency',
  },
  {
    title: 'Financial Report revenue',
    field: 'financialReport.revenue',
  },
  {
    title: 'Total funding',
    field: 'totalFunding',
  },
  {
    title: 'Tech',
    field: 'tech',
  },
  {
    title: 'Employees year',
    field: 'employees.year',
  },
  {
    title: 'Employees from',
    field: 'employees.from',
  },
  {
    title: 'Employees to',
    field: 'employees.to',
  },
  {
    title: 'LinkedIn Url',
    render: ({ socials }) => formatLink(socials?.linkedIn),
  },
  {
    title: 'Twitter Url',
    render: ({ socials }) => formatLink(socials?.twitter),
  },
  {
    title: 'Crunchbase Url',
    render: ({ socials }) => formatLink(socials?.crunchbase),
  },
  {
    title: 'Facebook Url',
    render: ({ socials }) => formatLink(socials?.facebook),
  },
]

const CompanyDetails = ({ data, handleSubmit }: CompanyDetailsProps) => {
  const updateCompany = useUpdateCompany()

  const onSubmit = ({ website }: any) => updateCompany.mutate({ uuid: data.uuid, url: website })

  return (
    <form onSubmit={handleSubmit?.(onSubmit)}>
      <Box sx={{ mb: '2rem' }}>
        <CardHeader title="Details" />
        <DescriptionListTable config={config} data={data} />
      </Box>
    </form>
  )
}

export default withForm({
  form: 'companyDetails',
  validate: ({ website }: any) => ({
    website: !website
      ? 'Website is required'
      : !isDomain(website)
        ? 'Enter a valid domain (e.g. example.com)'
        : undefined,
  }),
})(CompanyDetails)
